import { CriteriaTypeDetailVisibilityStateEnumValue } from 'enums/CriteriaTypeDetailVisibilityStateEnum';

/**
 * Determine whether a criteria type detail should be shown for the current submission context.
 * Mirrors the business rules used when rendering detail cards so counts stay in sync.
 *
 * @param {object} detail Criteria type detail record.
 * @param {object} context Submission context values.
 * @param {string|null} context.attemptedCriteriaOptionId Selected criteria option id.
 * @param {string|null} context.heroUnitProductLineId Selected hero unit product line id.
 * @param {string[]} context.dealerProductLineIds Dealer product line ids.
 * @param {Map} context.submissionDetailValueByDetailId Map of controlling detail values.
 * @param {boolean} context.hasAvailableCriteriaOptions Whether the criteria has selectable options.
 * @returns {boolean}
 */
export const detailAppliesToSubmission = (detail, context = {}) => {
  if (!detail) {
    return false;
  }

  const {
    attemptedCriteriaOptionId = null,
    heroUnitProductLineId = null,
    dealerProductLineIds = [],
    submissionDetailValueByDetailId,
    requiresHeroUnitSelection = true,
    hasAvailableCriteriaOptions = true,
  } = context;

  const optionIds = (detail.criteriaTypeDetailCriteriaOptions || []).map(join => join?.criteriaOption_Id).filter(Boolean);

  if (optionIds.length === 0 && hasAvailableCriteriaOptions) {
    return false;
  }

  if (optionIds.length > 0) {
    if (!attemptedCriteriaOptionId) {
      return false;
    }
    if (!optionIds.includes(attemptedCriteriaOptionId)) {
      return false;
    }
  }

  const productLineIds = (detail.criteriaTypeDetailProductLines || []).map(join => join?.productLine_Id).filter(Boolean);

  if (productLineIds.length > 0) {
    if (requiresHeroUnitSelection) {
      if (!heroUnitProductLineId || !productLineIds.includes(heroUnitProductLineId)) {
        return false;
      }
    } else if (heroUnitProductLineId && !productLineIds.includes(heroUnitProductLineId)) {
      return false;
    }
  }

  const dealerLineIds = (detail.criteriaTypeDetailDealerProductLines || [])
    .map(join => join?.productLine_Id)
    .filter(Boolean);

  if (dealerLineIds.length > 0) {
    if (!dealerProductLineIds.length || !dealerLineIds.some(id => dealerProductLineIds.includes(id))) {
      return false;
    }
  }

  const visibilityRules = detail.criteriaTypeDetailVisibilityRules || [];

  if (!visibilityRules.length) {
    return true;
  }

  const hasVisibleRules = visibilityRules.some(
    rule => rule?.visibilityState === CriteriaTypeDetailVisibilityStateEnumValue.Visible,
  );

  const matchedRules = visibilityRules.filter(rule => {
    if (!rule?.controllingCriteriaTypeDetail_Id || !rule?.criteriaTypeDetailPossibleValue_Id) {
      return false;
    }

    const controllingValue = submissionDetailValueByDetailId?.get?.(rule.controllingCriteriaTypeDetail_Id);

    if (controllingValue === undefined || controllingValue === null || controllingValue === '') {
      return false;
    }

    return String(controllingValue) === String(rule.criteriaTypeDetailPossibleValue_Id);
  });

  if (matchedRules.some(rule => rule?.visibilityState === CriteriaTypeDetailVisibilityStateEnumValue.Hidden)) {
    return false;
  }

  if (matchedRules.some(rule => rule?.visibilityState === CriteriaTypeDetailVisibilityStateEnumValue.Visible)) {
    return true;
  }

  if (hasVisibleRules) {
    return false;
  }

  return true;
};

/**
 * Order a flat list of criteria type details so only nodes reachable from the root (parent === null)
 * are included, preserving the hierarchy used by the UI when rendering cards.
 *
 * @param {object[]} details Flat array of criteria type details.
 * @returns {object[]} ordered list matching the rendered hierarchy.
 */
export const orderDetailsByHierarchy = (details = []) => {
  if (!details.length) {
    return [];
  }

  const byParent = new Map();

  details.forEach(detail => {
    if (!detail) {
      return;
    }
    const parentKey = detail.parentCriteriaTypeDetail_Id || null;
    const existing = byParent.get(parentKey) ?? [];
    existing.push(detail);
    byParent.set(parentKey, existing);
  });

  const ordered = [];

  const traverse = parentId => {
    const siblings = byParent.get(parentId);
    if (!siblings || !siblings.length) {
      return;
    }

    siblings
      .slice()
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .forEach(detail => {
        ordered.push(detail);
        traverse(detail.id);
      });
  };

  traverse(null);
  return ordered;
};
